import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft } from 'lucide-react';

const SwipeIndicator = ({ isSwiping, swipeProgress, currentIndex, totalSections }) => {
  const canSwipeLeft = currentIndex < totalSections - 1;
  const canSwipeRight = currentIndex > 0;

  return (
    <div className="mb-4 text-center md:hidden">
      <AnimatePresence mode="wait">
        {isSwiping && swipeProgress > 30 ? (
          // Active swipe state - show dynamic feedback
          <motion.div
            key="swiping"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="flex flex-col items-center gap-2"
          >
            <motion.p
              className="text-[11px] text-emerald-400/80 uppercase tracking-wider font-medium"
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            >
              Release to switch
            </motion.p>
            <div className="flex items-center gap-2">
              {canSwipeRight && (
                <motion.div
                  animate={{
                    x: [-3, 0, -3],
                    opacity: [0.4, 0.8, 0.4]
                  }}
                  transition={{
                    duration: 0.8,
                    repeat: Infinity
                  }}
                >
                  <ChevronLeft className="w-4 h-4 text-emerald-400/70" />
                </motion.div>
              )}
              <div className="w-32 h-1.5 bg-white/[0.08] rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-emerald-500/70 to-violet-500/70"
                  initial={{ width: '0%' }}
                  animate={{ width: `${Math.min(swipeProgress, 100)}%` }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              </div>
              {canSwipeLeft && (
                <motion.div
                  animate={{
                    x: [3, 0, 3],
                    opacity: [0.4, 0.8, 0.4]
                  }}
                  transition={{
                    duration: 0.8,
                    repeat: Infinity
                  }}
                >
                  <ChevronRight className="w-4 h-4 text-emerald-400/70" />
                </motion.div>
              )}
            </div>
            <p className="text-[9px] text-white/40 mt-1">
              {currentIndex + 1} of {totalSections}
            </p>
          </motion.div>
        ) : (
          // Default state - show hint
          <motion.div
            key="default"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col items-center gap-2"
          >
            <p className="text-[10px] text-white/50 uppercase tracking-wider">
              Swipe to navigate
            </p>
            <div className="flex items-center justify-center gap-1">
              {canSwipeRight && (
                <motion.div
                  animate={{
                    x: [-2, 0, -2],
                    opacity: [0.2, 0.4, 0.2]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "loop"
                  }}
                >
                  <ChevronLeft className="w-3 h-3 text-white/30" />
                </motion.div>
              )}
              <div className="flex gap-1 mx-2">
                {[...Array(totalSections)].map((_, idx) => (
                  <motion.div
                    key={idx}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      idx === currentIndex
                        ? 'bg-emerald-400/70 w-4'
                        : 'bg-white/20'
                    }`}
                    animate={
                      idx === currentIndex
                        ? { scale: [1, 1.2, 1] }
                        : {}
                    }
                    transition={
                      idx === currentIndex
                        ? { duration: 2, repeat: Infinity }
                        : {}
                    }
                  />
                ))}
              </div>
              {canSwipeLeft && (
                <motion.div
                  animate={{
                    x: [2, 0, 2],
                    opacity: [0.2, 0.4, 0.2]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "loop"
                  }}
                >
                  <ChevronRight className="w-3 h-3 text-white/30" />
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SwipeIndicator;